import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  CancelOutlined as RejectIcon,
  CheckCircleOutlined as ApproveIcon,
  FactCheckOutlined as ApprovalsIcon,
  LocalShippingOutlined as HandoffIcon,
  UndoOutlined as UndoIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import PageHeader from '../../components/PageHeader';
import ApproveOrderDialog, { type ApproveOrderTarget } from '../../components/orders/ApproveOrderDialog';
import RejectOrderDialog, { type RejectOrderTarget } from '../../components/orders/RejectOrderDialog';
import ReinstateDecisionDialog, { type ReinstateDecisionTarget } from '../../components/orders/ReinstateDecisionDialog';
import { APP_SHAPE } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { listPendingApprovalOrders, reinstateOrderDecision } from '../../services/order-approval.service';
import { useOrganizationStore } from '../../stores/organization.store';
import type { ApprovalOrderRow, OrderApprovalStatus } from '../../types/order-approval.type';
import { formatKg, formatSar, formatWhen } from '../../utils/formatters';

const HANDSHAKE_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'error',
  superseded: 'default',
};

export default function OrderApprovals() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { canMutate, isPlatformAdmin } = useAuthPermissions();
  const [rows, setRows] = useState<ApprovalOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<ApproveOrderTarget | null>(null);
  const [rejectTarget, setRejectTarget] = useState<RejectOrderTarget | null>(null);
  const [reinstateTarget, setReinstateTarget] = useState<ReinstateDecisionTarget | null>(null);
  const [reinstating, setReinstating] = useState(false);
  const [reinstateError, setReinstateError] = useState<string | null>(null);

  // Filters: status + date go to the server; point/supplier narrow the loaded rows.
  const [status, setStatus] = useState<OrderApprovalStatus>('pending');
  const [runDate, setRunDate] = useState<Date | null>(null);
  const [pointFilter, setPointFilter] = useState<string | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);

  const orgId = selectedOrganization?.id;
  const canApprove = Boolean(orgId && canMutate(`recyclee.${orgId}.*.order_approval.approve`));
  const canReinstate = Boolean(isPlatformAdmin);

  const submitReinstate = async (reason: string) => {
    if (!reinstateTarget) return;
    setReinstating(true);
    setReinstateError(null);
    try {
      await reinstateOrderDecision(reinstateTarget.pickupOrderId, reason);
      setReinstateTarget(null);
      await load();
    } catch (err) {
      setReinstateError(
        (err as { response?: { data?: { response?: { message?: string } } } })?.response?.data?.response?.message ??
          t('orderApprovals.reinstate.title')
      );
    } finally {
      setReinstating(false);
    }
  };
  const locale = i18n.resolvedLanguage || i18n.language;

  const load = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    listPendingApprovalOrders(orgId, {
      approvalStatus: status,
      runDate: runDate ? format(runDate, 'yyyy-MM-dd') : undefined,
    })
      .then((page) => setRows(page.orders))
      .catch(() => setError(t('orderApprovals.loadError')))
      .finally(() => setLoading(false));
  }, [orgId, status, runDate, t]);

  useEffect(() => { load(); }, [load]);

  const pointOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.point_name).filter((v): v is string => Boolean(v)))).sort(),
    [rows],
  );
  const supplierOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.supplier_name).filter((v): v is string => Boolean(v)))).sort(),
    [rows],
  );
  const visible = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!pointFilter || row.point_name === pointFilter) &&
          (!supplierFilter || row.supplier_name === supplierFilter),
      ),
    [rows, pointFilter, supplierFilter],
  );

  const openApprove = (row: ApprovalOrderRow) =>
    setTarget({
      pickupOrderId: row.pickup_order_id,
      pointName: row.point_name,
      supplierName: row.supplier_name,
      weightKg: row.verified_weight_kg ?? row.estimated_weight,
      confirmation: row.pickup_approval_status
        ? { status: row.pickup_approval_status, name: row.pickup_approval_name }
        : null,
    });

  const openReject = (row: ApprovalOrderRow) =>
    setRejectTarget({
      pickupOrderId: row.pickup_order_id,
      pointName: row.point_name,
      supplierName: row.supplier_name,
      weightKg: row.verified_weight_kg ?? row.estimated_weight,
    });

  if (!selectedOrganization) return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;

  return (
    <Box>
      <PageHeader
        icon={<ApprovalsIcon />}
        title={t('orderApprovals.pageTitle')}
        subtitle={t('orderApprovals.subtitle')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
      />

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 220, flex: 1 }}
            options={pointOptions}
            value={pointFilter}
            onChange={(_, value) => setPointFilter(value)}
            renderInput={(params) => <TextField {...params} label={t('orderDetails.point')} />}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 220, flex: 1 }}
            options={supplierOptions}
            value={supplierFilter}
            onChange={(_, value) => setSupplierFilter(value)}
            renderInput={(params) => <TextField {...params} label={t('suppliers.collectionPoints.table.supplier')} />}
          />
          <DatePicker
            label={t('trips.dateLabel')}
            value={runDate}
            onChange={(value) => setRunDate(value)}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } }, field: { clearable: true } }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="approval-status-label">{t('collectionsMonitor.status')}</InputLabel>
            <Select
              labelId="approval-status-label"
              label={t('collectionsMonitor.status')}
              value={status}
              onChange={(event) => setStatus(event.target.value as OrderApprovalStatus)}
            >
              <MenuItem value="pending">{t('collectionsMonitor.approvalStatus.pending')}</MenuItem>
              <MenuItem value="approved">{t('collectionsMonitor.approvalStatus.approved')}</MenuItem>
              <MenuItem value="rejected">{t('collectionsMonitor.approvalStatus.rejected')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={load}>{t('suppliers.details.retry')}</Button>}>{error}</Alert> : null}

      <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            {t('orderApprovals.empty')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('orderDetails.point')}</TableCell>
                  <TableCell>{t('suppliers.collectionPoints.table.supplier')}</TableCell>
                  <TableCell>{t('orderDetails.wasteType')}</TableCell>
                  <TableCell align="right">{t('orderDetails.weight')}</TableCell>
                  <TableCell align="right">{t('orderDetails.price')}</TableCell>
                  <TableCell>{t('orderApprovals.pickupApprovalCol')}</TableCell>
                  <TableCell>{t('orderApprovals.completedAt')}</TableCell>
                  <TableCell align="right">{t('suppliers.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((row) => (
                  <TableRow key={row.pickup_order_id} hover>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {row.handoff ? (
                        <Tooltip title={t('orderApprovals.handoffPointHint', { org: row.handoff.from_org_name ?? '' })}>
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<HandoffIcon fontSize="small" />}
                            label={t('orderApprovals.viaOrg', { org: row.handoff.from_org_name ?? '—' })}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" noWrap title={row.point_name ?? ''}>{row.point_name ?? '—'}</Typography>
                      )}
                      {row.approval_status === 'rejected' && row.rejection_reason ? (
                        <Typography variant="caption" color="error.main" display="block" noWrap title={row.rejection_reason}>
                          {t('orderApprovals.rejectedBecause', { reason: row.rejection_reason })}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" noWrap title={row.supplier_name ?? ''}>{row.supplier_name ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>{row.waste_type || '—'}</TableCell>
                    <TableCell align="right">{formatKg(row.verified_weight_kg ?? row.estimated_weight, locale)}</TableCell>
                    <TableCell align="right">
                      {formatSar(row.sale_amount, locale)}
                      {row.flags.includes('missing_agreement_price') ? (
                        <Chip size="small" color="warning" label={t('orderDetails.missingPrice')} sx={{ ml: 0.5 }} />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.pickup_approval_status ? (
                        <Tooltip title={row.pickup_approval_name ?? ''}>
                          <Chip
                            size="small"
                            color={HANDSHAKE_COLOR[row.pickup_approval_status] ?? 'default'}
                            label={t(`orderDetails.approvalStatus.${row.pickup_approval_status}`)}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">{t('orderApprovals.noHandshake')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatWhen(row.completed_at, locale)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('monitoring.orders.viewEvidence')}>
                        <IconButton size="small" onClick={() => navigate(`/orders/${row.pickup_order_id}`)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canApprove && row.approval_status === 'pending' ? (
                        <>
                          <Tooltip title={t('orderDetails.approve')}>
                            <IconButton size="small" color="success" onClick={() => openApprove(row)}>
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('orderDetails.reject')}>
                            <IconButton size="small" color="error" onClick={() => openReject(row)}>
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                      {canReinstate && (row.approval_status === 'approved' || row.approval_status === 'rejected') ? (
                        <Tooltip title={t('orderApprovals.reinstate.action')}>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() =>
                              setReinstateTarget({
                                pickupOrderId: row.pickup_order_id,
                                wasteType: row.waste_type,
                                approvalStatus: row.approval_status,
                              })
                            }
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {orgId ? (
        <>
          <ApproveOrderDialog
            open={Boolean(target)}
            organizationId={orgId}
            target={target}
            onClose={() => setTarget(null)}
            onApproved={load}
          />
          <RejectOrderDialog
            open={Boolean(rejectTarget)}
            organizationId={orgId}
            target={rejectTarget}
            onClose={() => setRejectTarget(null)}
            onRejected={load}
          />
        </>
      ) : null}

      <ReinstateDecisionDialog
        open={!!reinstateTarget}
        submitting={reinstating}
        target={reinstateTarget}
        error={reinstateError}
        onClose={() => {
          setReinstateTarget(null);
          setReinstateError(null);
        }}
        onConfirm={(reason) => void submitReinstate(reason)}
      />
    </Box>
  );
}
